import { useRef, useState, type ChangeEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAttachments, useDeleteAttachment, useInventoryItem, useUploadAttachment } from "../../api/inventory";
import { useUserPicture } from "../../api/users";
import { Avatar } from "../../components/Avatar";
import BackButton from "../../components/ui/BackButton";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { Card } from "../../components/ui/Card";
import { Modal } from "../../components/ui/Modal";
import { Page } from "../../components/ui/Page";
import PageLoader from "../../components/ui/PageLoader";
import { Spinner } from "../../components/ui/Spinner";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { useLayoutTitle } from "../../hooks/useLayoutTitle";
import { useTranslation } from "../../hooks/useTranslation";
import { rejectCauseLabel, statusLabel } from "../../types/inventory";
import { formatUserName } from "../../types/user";
import { formatDate } from "../../utils/date";
import { formatBytes } from "../../utils/file";
import styles from "./ViewInventoryPage.module.css";

export default function ViewInventoryPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: item, isLoading } = useInventoryItem(Number(id) ?? 0);
  useLayoutTitle(
    item ? t("inventory.viewInventoryPageTitle", { id: item.id, title: item.name }) : t("inventory.allItems"),
  );
  const currentUser = useCurrentUser();
  const { data: attachments = [], isLoading: attLoading } = useAttachments(Number(id) ?? 0);
  const uploadAttachment = useUploadAttachment(Number(id) ?? 0);
  const deleteAttachment = useDeleteAttachment(Number(id) ?? 0);
  const createdByPicture = useUserPicture(item?.createdBy.id);
  const fileRef = useRef<HTMLInputElement>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [attachmentToDelete, setAttachmentToDelete] = useState<string | null>(null);

  function handleFileChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) {
      uploadAttachment.mutate(file, {
        onSuccess: () => {
          if (fileRef.current) fileRef.current.value = "";
        },
      });
    }
  }

  function confirmDeleteAttachment(attachmentId: string) {
    setAttachmentToDelete(attachmentId);
    setDeleteModalOpen(true);
  }

  function handleDeleteAttachment() {
    if (!attachmentToDelete) return;
    deleteAttachment.mutate(attachmentToDelete, {
      onSuccess: () => {
        setDeleteModalOpen(false);
        setAttachmentToDelete(null);
      },
    });
  }

  if (isLoading) return <PageLoader />;
  if (!item) return <p className={styles.emptyText}>{t("common.noData")}</p>;

  const isAuthor = currentUser?.id === item.createdBy.id;
  const isAdmin = currentUser?.isAdmin === true;
  const canEdit = (isAuthor && item.status === "under_review") || isAdmin;
  const canUploadAttachment = isAdmin || isAuthor;
  const canDeleteAttachment = isAdmin || (isAuthor && item.status === "under_review");

  return (
    <Page width="wide">
      <Card>
        {/* Header */}
        <div className={styles.cardHeaderActions}>
          <div>
            <BackButton label={t("inventory.backToInventories")} onClick={() => navigate("/inventory")} />
          </div>
          <Badge variant={item.status} label={statusLabel(item.status, t)} />
          {canEdit && (
            <Button variant="secondary" onClick={() => navigate(`/inventory/${id}/edit`)}>
              {t("common.edit")}
            </Button>
          )}
        </div>

        <div className={styles.divider} />

        {/* Description */}
        <div className={styles.descSection}>
          <span className={styles.sectionLabel}>{t("inventory.description")}</span>
          <p className={styles.descText}>{item.description}</p>
        </div>

        {item.improvementNotes && (
          <div className={styles.descSection}>
            <span className={styles.sectionLabel}>{t("inventory.improvementNotes")}</span>
            <p className={styles.descText}>{item.improvementNotes}</p>
          </div>
        )}

        {/* Metadata tiles */}
        <div className={styles.statsGrid}>
          <div className={styles.statTile}>
            <span className={styles.statTileLabel}>{t("inventory.audience")}</span>
            <Badge
              variant={item.audience}
              label={item.audience === "external" ? t("inventory.audienceExternal") : t("inventory.audienceInternal")}
            />
          </div>
          <div className={styles.statTile}>
            <span className={styles.statTileLabel}>{t("inventory.deploymentLocation")}</span>
            {item.deploymentLocation ? (
              <span className={styles.statTileText}>{item.deploymentLocation}</span>
            ) : (
              <span className={styles.statTileMuted}>{t("common.notSet")}</span>
            )}
          </div>
          <div className={styles.statTile}>
            <span className={styles.statTileLabel}>{t("inventory.githubUrl")}</span>
            {item.githubUrl ? (
              <a href={item.githubUrl} target="_blank" rel="noopener noreferrer" className={styles.linkText}>
                GitHub ↗
              </a>
            ) : (
              <span className={styles.statTileMuted}>{t("common.notSet")}</span>
            )}
          </div>
        </div>

        <div className={styles.divider} />

        {/* Participants */}
        <div className={styles.inlineRow}>
          <div className={styles.inlineGroup}>
            <span className={styles.inlineLabel}>{t("inventory.participants")}</span>
            {item.participants.length > 0 ? (
              <div className={styles.chipList}>
                {item.participants.map((p) => (
                  <span key={p.id} className={styles.chip}>
                    {formatUserName(p)}
                  </span>
                ))}
              </div>
            ) : (
              <span className={styles.inlineMuted}>{t("common.notSet")}</span>
            )}
          </div>
        </div>

        {item.rejectCause && (
          <div className={`${styles.row} ${styles.inlineRowBorder}`}>
            <span className={styles.inlineLabel}>{t("inventory.rejectCause")}</span>
            <span className={styles.inlineValue}>
              <Badge variant={item.status} label={rejectCauseLabel(item.rejectCause, t)} />
            </span>
          </div>
        )}
        {item.rejectNote && (
          <div className={`${styles.row} ${styles.inlineRowBorder}`}>
            <span className={styles.inlineLabel}>{t("inventory.rejectNote")}</span>
            <span className={styles.inlineValue}>{item.rejectNote}</span>
          </div>
        )}

        <div className={styles.divider} />

        {/* Created by footer */}
        <div className={styles.footer}>
          <div className={styles.footerCreator}>
            <Avatar user={item.createdBy} pictureUrl={createdByPicture.data} />
            <div className={styles.footerMeta}>
              <span className={styles.footerMetaLabel}>{t("inventory.createdBy")}</span>
              <span className={styles.footerMetaValue}>{formatUserName(item.createdBy)}</span>
            </div>
          </div>
          <div className={styles.footerDate}>
            <span className={styles.footerMetaLabel}>{t("inventory.createdAt")}</span>
            <span className={styles.footerMetaValue}>{formatDate(item.createdAt)}</span>
          </div>
        </div>
      </Card>

      {/* Attachments */}
      <Card>
        <div className={styles.attachmentHeader}>
          <span className={styles.attachmentSectionLabel}>{t("inventory.attachments")}</span>
          {canUploadAttachment && (
            <div>
              <input
                ref={fileRef}
                type="file"
                id="file-upload"
                className={styles.hiddenInput}
                onChange={handleFileChange}
              />
              <Button
                variant="secondary"
                size="sm"
                loading={uploadAttachment.isPending}
                onClick={() => fileRef.current?.click()}
                type="button"
              >
                {t("inventory.uploadAttachment")}
              </Button>
            </div>
          )}
        </div>
        {attLoading ? (
          <Spinner size="sm" />
        ) : attachments.length === 0 ? (
          <p className={styles.emptyAttachments}>{t("inventory.noAttachments")}</p>
        ) : (
          <ul className={styles.attachmentList}>
            {attachments.map((attachment) => (
              <li key={attachment.id} className={styles.attachmentItem}>
                <div>
                  <p className={styles.attachmentName}>{attachment.fileName}</p>
                  <p className={styles.attachmentMeta}>
                    {formatBytes(attachment.sizeBytes)} &middot; {t("inventory.uploadedBy")}{" "}
                    {formatUserName(attachment.uploadedBy)} &middot; {formatDate(attachment.uploadedAt)}
                  </p>
                </div>
                {canDeleteAttachment && (
                  <Button variant="danger" size="sm" onClick={() => confirmDeleteAttachment(attachment.id)}>
                    {t("common.delete")}
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Modal
        open={deleteModalOpen}
        title={t("inventory.deleteAttachment")}
        onClose={() => setDeleteModalOpen(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setDeleteModalOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button variant="danger" onClick={handleDeleteAttachment} loading={deleteAttachment.isPending}>
              {t("common.delete")}
            </Button>
          </>
        }
      >
        <p className={styles.modalText}>{t("inventory.confirmDeleteAttachment")}</p>
      </Modal>
    </Page>
  );
}
